<template>
  <AeModal :model-value="modelValue" @update:model-value="$emit('update:modelValue', $event)" title="Immagine" size="large">
    <div
      class="izm-viewport"
      @wheel.prevent="onWheel"
      @mousedown="onMouseDown"
      @mousemove="onMouseMove"
      @mouseup="onMouseUp"
      @mouseleave="onMouseUp"
      @dblclick="resetView"
    >
      <img
        v-if="src"
        ref="imgEl"
        :src="src"
        class="izm-img"
        :class="{ 'izm-img--dragging': dragging }"
        :style="imgStyle"
        draggable="false"
        @dragstart.prevent
      />
      <div class="izm-zoom-ctrl">
        <button type="button" @click="applyZoom(1.2)" title="Zoom +">+</button>
        <button type="button" @click="applyZoom(1 / 1.2)" title="Zoom −">−</button>
        <button type="button" @click="resetView" title="Adatta vista">⊡</button>
      </div>
    </div>

    <template v-if="items.length > 1" #footer>
      <div class="izm-nav">
        <button type="button" class="izm-nav-btn" :disabled="index <= 0" @click="go(-1)" title="Precedente">
          ‹
        </button>
        <div class="izm-nav-info">
          <span class="izm-nav-date">{{ formatDate(current?.date) }}</span>
          <span class="izm-nav-count">{{ current?.trap ?? '—' }} catture</span>
        </div>
        <button type="button" class="izm-nav-btn" :disabled="index >= items.length - 1" @click="go(1)" title="Successiva">
          ›
        </button>
      </div>
    </template>
  </AeModal>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { AeModal } from '@aevue/components'
import { BASE_URL } from '../services/api.js'

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  // Elenco delle catture con immagine, in ordine di navigazione: [{ guid, date, trap }]
  items:      { type: Array,   default: () => [] },
  startIndex: { type: Number,  default: 0 },
})
defineEmits(['update:modelValue'])

const index = ref(0)

const current = computed(() => props.items[index.value] || null)
const src = computed(() => current.value ? `${BASE_URL}/static/uploads/images/${current.value.guid}.png` : '')

function go(delta) {
  const next = index.value + delta
  if (next < 0 || next >= props.items.length) return
  index.value = next
}

function formatDate(d) {
  if (!d) return '—'
  return new Date(d).toLocaleDateString('it-IT')
}

const zoom     = ref(1)
const panX     = ref(0)
const panY     = ref(0)
const originX  = ref(50)  // % — punto su cui pivota lo scale
const originY  = ref(50)
const dragging = ref(false)
const dragStart = { x: 0, y: 0, panX: 0, panY: 0 }
const imgEl = ref(null)

const MIN_ZOOM = 1
const MAX_ZOOM = 6

function resetView() {
  zoom.value = 1
  panX.value = 0
  panY.value = 0
  originX.value = 50
  originY.value = 50
}

// Apertura modale o cambio immagine (navigazione avanti/indietro): riparti da startIndex
// e azzera lo zoom/pan correnti.
watch(() => props.modelValue, (open) => {
  if (open) index.value = props.startIndex
})
watch(index, resetView)

function applyZoom(factor) {
  const next = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, zoom.value * factor))
  zoom.value = next
  if (next === MIN_ZOOM) { panX.value = 0; panY.value = 0; originX.value = 50; originY.value = 50 }
}

// Zoom centrato sul punto del cursore: sposta il transform-origin lì prima di scalare,
// così l'immagine "cresce" a partire da dove si trova il mouse (getBoundingClientRect
// riflette già la dimensione visuale corrente, quindi la % resta corretta a ogni zoom level).
function onWheel(e) {
  const rect = imgEl.value?.getBoundingClientRect()
  if (rect && rect.width && rect.height) {
    originX.value = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100))
    originY.value = Math.max(0, Math.min(100, ((e.clientY - rect.top) / rect.height) * 100))
  }
  applyZoom(e.deltaY < 0 ? 1.15 : 1 / 1.15)
}

function onMouseDown(e) {
  if (zoom.value <= MIN_ZOOM) return
  dragging.value = true
  dragStart.x = e.clientX
  dragStart.y = e.clientY
  dragStart.panX = panX.value
  dragStart.panY = panY.value
}

function onMouseMove(e) {
  if (!dragging.value) return
  panX.value = dragStart.panX + (e.clientX - dragStart.x)
  panY.value = dragStart.panY + (e.clientY - dragStart.y)
}

function onMouseUp() {
  dragging.value = false
}

const imgStyle = computed(() => ({
  transform: `translate(${panX.value}px, ${panY.value}px) scale(${zoom.value})`,
  transformOrigin: `${originX.value}% ${originY.value}%`,
  cursor: zoom.value > MIN_ZOOM ? (dragging.value ? 'grabbing' : 'grab') : 'default',
}))
</script>

<style scoped>
.izm-viewport {
  position: relative;
  width: 100%;
  height: 70vh;
  overflow: hidden;
  background: #111;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.izm-img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  transition: transform 0.05s linear;
  user-select: none;
}
.izm-img--dragging { transition: none; }

.izm-zoom-ctrl {
  position: absolute;
  top: 10px;
  right: 10px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.izm-zoom-ctrl button {
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.9);
  color: #222;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  line-height: 1;
}
.izm-zoom-ctrl button:hover { background: #fff; }

/* Navigatore footer */
.izm-nav {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  width: 100%;
}
.izm-nav-btn {
  border: 1px solid var(--border-color, #ddd);
  background: var(--ae-surface, #fff);
  border-radius: 6px;
  width: 32px;
  height: 32px;
  font-size: 1.2rem;
  line-height: 1;
  cursor: pointer;
}
.izm-nav-btn:disabled { opacity: 0.35; cursor: default; }
.izm-nav-btn:not(:disabled):hover { background: var(--ae-bg, #f5f5f5); }

.izm-nav-info {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 120px;
  font-size: 0.85rem;
}
.izm-nav-date  { font-weight: 600; }
.izm-nav-count { opacity: 0.65; font-size: 0.78rem; }
</style>
